import React, {useRef, useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrButton, {
  ArrButtonSize,
  ArrButtonVariant,
} from '../../common/arr-button/ArrButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../components/common/arr-menu/ArrMenu';
import {setSelectedAction} from '../../../redux/slice/actionButtonSlice';
import {t} from 'i18next';
import './ArrActionButton.scss';

export interface ActionMenuProps {
  size?: ArrButtonSize;
  header?: string;
  menuItems: CustomMenuItem[];
  isBtnDisabled?: boolean;
  direction?:
    | 'bottom'
    | 'top'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right';
  title?: string;
  dataTestId: string;
  autoFocusMenu?: boolean;
  disableAutoFocusItem?: boolean;
  subMenuDirection?: 'left' | 'right';
  variant?: ArrButtonVariant;
  onActionPick?: (item: CustomMenuItem) => void;
  menuClassName?: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  header,
  menuItems,
  size,
  direction,
  isBtnDisabled,
  title,
  dataTestId,
  autoFocusMenu,
  disableAutoFocusItem,
  subMenuDirection,
  variant = 'primary',
  onActionPick,
  menuClassName,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedMenuValue, setSelectedMenuValue] = useState<
    CustomMenuItem | undefined
  >(undefined);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSelectedAction({label: '', id: ''}));
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<ArrMenuHandle | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    lastTriggerRef.current = e.currentTarget;
    menuRef.current?.open(e.currentTarget as HTMLElement);
    setIsMenuOpen(true);
  };

  const onItemClick = (item: CustomMenuItem) => {
    setSelectedMenuValue(item);
    onActionPick?.(item);
    dispatch(setSelectedAction(item));
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <>
      <ArrButton
        ref={buttonRef}
        variant={variant}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        size={size ? size : 'small'}
        endIcon={
          <ArrowDropDownIcon
            style={{transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'}}
          />
        }
        onClick={handleClick}
        dataTestId={dataTestId ?? 'action-btn'}
        className={`${isMenuOpen ? 'arr-action-btn-open' : 'arr-action-btn'} ${isBtnDisabled ? 'btn-disabled' : ''}`}
        isAriaDisabled={isBtnDisabled}
      >
        {title ?? t('actionButton.actionLabel')}
      </ArrButton>
      <ArrMenu
        id="arr-menu-2"
        ref={menuRef}
        items={menuItems}
        onItemClick={onItemClick}
        className={`arr-action-menu ${menuClassName ? menuClassName : ''}`}
        direction={direction}
        title={header}
        autoFocusMenu={autoFocusMenu}
        disableAutoFocusItem={disableAutoFocusItem}
        onClose={() => {
          setIsMenuOpen(false);
          if (lastTriggerRef.current) {
            requestAnimationFrame(() => {
              lastTriggerRef.current?.focus();
              lastTriggerRef.current = null;
            });
          }
        }}
        subMenuDirection={subMenuDirection}
      />
    </>
  );
};

export default ActionMenu;
